import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DriveFolderUploadOutlinedIcon,
  CreateNewFolderOutlinedIcon,
  Field,
  FolderIcon,
  InsertDriveFileIcon,
  Input,
  MoreVertIcon,
  Spinner,
  Text,
  UploadFileIcon,
} from '@billda/design-system';
import ContextMenu, {
  type ContextMenuItem,
} from '../../../../shared/components/ui/ContextMenu';
import { TableCheckbox } from '../../../../shared/components/table/atoms/TableCheckbox';
import { useFolderContents } from '../../hooks/useFolderContents';
import { useSitePanelState } from '../../hooks/useSitePanelState';
import { useFileSelectionStore } from '../../stores/fileSelectionStore';
import { useFolderUpload } from '../../hooks/useFolderUpload';
import { collectInputFiles, getDropEntries } from '../../utils/folderUpload';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { BILLDA_MOVE_MIME } from '../../hooks/useDragAndDrop';
import { useDeleteFile } from '../../hooks/useDeleteFile';
import {
  useRenameFolder,
  useRenameFile,
  useDeleteFolder,
} from '../../hooks/useFolderMutations';
import { useFileDownload } from '../../hooks/useFileDownload';
import type { FolderDto, SiteFileDto } from '../../api/filesApi';
import { getFileIcon } from './fileIcons';
import { formatFileSize } from './formatFileSize';

interface FolderBrowserProps {
  siteId: number;
  folderId?: string;
  onNavigate: (folderId?: string) => void;
  /** Navigate up from site root (e.g. back to site list). */
  onNavigateUp?: () => void;
  canWrite: boolean;
  uploadButtonRef?: React.RefObject<HTMLInputElement | null>;
  /** Ref to the hidden folder <input>, so the parent can trigger folder selection. */
  folderInputRef?: React.RefObject<HTMLInputElement | null>;
  /** Hide the internal breadcrumb when the parent page renders its own. */
  hideBreadcrumb?: boolean;
  /** Called when the user chooses "New folder" from the background context menu. */
  onCreateFolder?: () => void;
}

type MenuTarget =
  | { type: 'folder'; item: FolderDto }
  | { type: 'file'; item: SiteFileDto }
  | { type: 'background' };

export function FolderBrowser({
  siteId,
  folderId,
  onNavigate,
  onNavigateUp,
  canWrite,
  uploadButtonRef,
  folderInputRef,
  hideBreadcrumb = false,
  onCreateFolder,
}: FolderBrowserProps) {
  const { data, isLoading } = useFolderContents(siteId, folderId);
  const { openPanel } = useSitePanelState();
  const { upload, uploadEntries, isPreparing } = useFolderUpload(
    siteId,
    folderId
  );
  const internalRef = useRef<HTMLInputElement>(null);
  const fileInputRef = uploadButtonRef ?? internalRef;
  const internalFolderRef = useRef<HTMLInputElement>(null);
  const folderInputEl = folderInputRef ?? internalFolderRef;

  const [isFileDropOver, setIsFileDropOver] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    type: 'file' | 'folder';
    name: string;
  } | null>(null);
  const [renameName, setRenameName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    type: 'file' | 'folder';
    name: string;
  } | null>(null);

  const deleteFile = useDeleteFile(siteId);
  const deleteFolder = useDeleteFolder(siteId);
  const renameFolderMutation = useRenameFolder(siteId);
  const renameFileMutation = useRenameFile();
  const downloadFile = useFileDownload();

  const {
    toggle,
    toggleRange,
    selectAll,
    clearAll,
    isSelected,
    selectedItems,
    lastSelectedIndex,
  } = useFileSelectionStore();

  const { getDragProps, getDropProps, isDragOver } = useDragAndDrop(
    siteId,
    folderId ?? null
  );

  // Clear selection on folder navigation
  useEffect(() => {
    clearAll();
  }, [folderId, clearAll]);

  // Prevent browser default file-drop behavior
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  // File / folder upload handling. collectInputFiles reads webkitRelativePath,
  // so the same handler serves both the plain file input and the folder input.
  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      const files = Array.from(event.target.files);
      event.target.value = '';
      void upload(collectInputFiles(files));
    },
    [upload]
  );

  // Drag-and-drop file upload (OS files, not in-app moves)
  const handleFileDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (canWrite && !event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        setIsFileDropOver(true);
      }
    },
    [canWrite]
  );

  const handleFileDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (canWrite && !event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        setIsFileDropOver(true);
      }
    },
    [canWrite]
  );

  const handleFileDragLeave = useCallback((event: React.DragEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    const currentTarget = event.currentTarget as Node;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsFileDropOver(false);
    }
  }, []);

  const handleFileDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsFileDropOver(false);
      // Only handle OS file/folder drops, not in-app moves.
      if (event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        return;
      }
      if (!canWrite) {
        return;
      }
      // Snapshot entries synchronously — the DataTransfer is cleared on return.
      const entries = getDropEntries(event.dataTransfer);
      if (entries.length > 0) {
        void uploadEntries(entries);
      } else if (event.dataTransfer.files.length > 0) {
        void upload(collectInputFiles(event.dataTransfer.files));
      }
    },
    [canWrite, upload, uploadEntries]
  );

  // Build a flat list of all items for shift+click range selection
  const allItems = useMemo(() => {
    if (!data) {
      return [];
    }
    const folders = data.folders.map(f => ({
      id: f.id,
      type: 'folder' as const,
    }));
    const files = data.files.map(f => ({ id: f.id, type: 'file' as const }));
    return [...folders, ...files];
  }, [data]);

  const allSelected =
    allItems.length > 0 && selectedItems.size === allItems.length;
  const someSelected = selectedItems.size > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearAll();
    } else {
      selectAll(allItems);
    }
  }, [allSelected, clearAll, selectAll, allItems]);

  const handleCheckboxClick = useCallback(
    (
      event: React.MouseEvent,
      id: string,
      type: 'file' | 'folder',
      index: number
    ) => {
      event.stopPropagation();
      if (event.shiftKey && lastSelectedIndex !== null) {
        toggleRange(lastSelectedIndex, index, allItems);
      } else {
        toggle(id, type);
        useFileSelectionStore.setState({ lastSelectedIndex: index });
      }
    },
    [lastSelectedIndex, toggleRange, toggle, allItems]
  );

  const handleRename = useCallback(() => {
    const trimmed = renameName.trim();
    if (!trimmed || !renameTarget) {
      return;
    }
    if (renameTarget.type === 'folder') {
      renameFolderMutation.mutate(
        { folderId: renameTarget.id, name: trimmed },
        { onSuccess: () => setRenameTarget(null) }
      );
    } else {
      renameFileMutation.mutate(
        { fileId: renameTarget.id, name: trimmed },
        { onSuccess: () => setRenameTarget(null) }
      );
    }
  }, [renameName, renameTarget, renameFolderMutation, renameFileMutation]);

  const [menuState, setMenuState] = useState<{
    open: boolean;
    target: MenuTarget | null;
    anchorPosition?: { top: number; left: number };
    anchorEl?: HTMLElement | null;
  }>({ open: false, target: null });

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, target: MenuTarget) => {
      event.preventDefault();
      event.stopPropagation();
      setMenuState({
        open: true,
        target,
        anchorPosition: { top: event.clientY, left: event.clientX },
        anchorEl: undefined,
      });
    },
    []
  );

  const handleOverflowClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, target: MenuTarget) => {
      event.stopPropagation();
      setMenuState({
        open: true,
        target,
        anchorEl: event.currentTarget,
        anchorPosition: undefined,
      });
    },
    []
  );

  const closeMenu = useCallback(() => {
    setMenuState({ open: false, target: null });
  }, []);

  // Top-level right-click handler for the folder browser.
  // Handles two scenarios:
  //   1. Normal right-click (no menu open) — show the background menu.
  //   2. Portal right-click (menu already open, event bubbles through React's
  //      portal tree) — peek under the backdrop with elementsFromPoint and
  //      switch to the appropriate context or close.
  const handleBoxContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const boxEl = e.currentTarget;
      const isPortalEvent = !boxEl.contains(e.target as Node);

      if (isPortalEvent) {
        e.preventDefault();
        e.stopPropagation();

        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        const realTarget = elements.find(el => boxEl.contains(el)) as
          | HTMLElement
          | undefined;

        if (!realTarget) {
          closeMenu();
          return;
        }

        const folderRow = realTarget.closest('[data-testid^="folder-row-"]');
        if (folderRow && data) {
          const id = folderRow
            .getAttribute('data-testid')!
            .replace('folder-row-', '');
          const folder = data.folders.find(f => f.id === id);
          if (folder) {
            handleContextMenu(e, { type: 'folder', item: folder });
            return;
          }
        }

        const fileRow = realTarget.closest('[data-testid^="file-row-"]');
        if (fileRow && data) {
          const id = fileRow
            .getAttribute('data-testid')!
            .replace('file-row-', '');
          const file = data.files.find(f => f.id === id);
          if (file) {
            handleContextMenu(e, { type: 'file', item: file });
            return;
          }
        }

        handleContextMenu(e, { type: 'background' });
        return;
      }

      handleContextMenu(e, { type: 'background' });
    },
    [data, handleContextMenu, closeMenu]
  );

  const getMenuItems = useCallback((): ContextMenuItem[] => {
    if (!menuState.target) {
      return [];
    }

    if (menuState.target.type === 'background') {
      const items: ContextMenuItem[] = [];
      if (canWrite && onCreateFolder) {
        items.push({
          key: 'new-folder',
          label: 'New folder',
          icon: <CreateNewFolderOutlinedIcon className="size-4" />,
          onClick: onCreateFolder,
        });
      }
      if (canWrite) {
        items.push(
          {
            key: 'upload',
            label: 'Upload files',
            icon: <UploadFileIcon className="size-4" />,
            onClick: () => fileInputRef.current?.click(),
          },
          {
            key: 'upload-folder',
            label: 'Upload folder',
            icon: <DriveFolderUploadOutlinedIcon className="size-4" />,
            onClick: () => folderInputEl.current?.click(),
          }
        );
      }
      return items;
    }

    if (menuState.target.type === 'folder') {
      const folder = menuState.target.item as FolderDto;
      const items: ContextMenuItem[] = [
        {
          key: 'open',
          label: 'Open',
          onClick: () => onNavigate(folder.id),
        },
      ];
      if (canWrite) {
        items.push(
          {
            key: 'rename',
            label: 'Rename',
            onClick: () => {
              setRenameTarget({
                id: folder.id,
                type: 'folder',
                name: folder.name,
              });
              setRenameName(folder.name);
            },
          },
          {
            key: 'delete',
            label: 'Delete',
            onClick: () =>
              setDeleteTarget({
                id: folder.id,
                type: 'folder',
                name: folder.name,
              }),
          }
        );
      }
      return items;
    }

    // File menu
    const file = menuState.target.item as SiteFileDto;
    const items: ContextMenuItem[] = [
      {
        key: 'view-details',
        label: 'View details',
        onClick: () => openPanel('file', file.id),
      },
    ];
    if (canWrite) {
      items.push(
        {
          key: 'rename',
          label: 'Rename',
          onClick: () => {
            setRenameTarget({ id: file.id, type: 'file', name: file.name });
            setRenameName(file.name);
          },
        },
        {
          key: 'download',
          label: 'Download',
          dividerAfter: true,
          onClick: () => downloadFile.mutate(file.id),
        },
        {
          key: 'delete',
          label: 'Delete',
          onClick: () =>
            setDeleteTarget({ id: file.id, type: 'file', name: file.name }),
        }
      );
    } else {
      items.push({
        key: 'download',
        label: 'Download',
        onClick: () => downloadFile.mutate(file.id),
      });
    }
    return items;
  }, [
    menuState.target,
    canWrite,
    onNavigate,
    openPanel,
    deleteFile,
    deleteFolder,
    downloadFile,
    onCreateFolder,
    fileInputRef,
    folderInputEl,
  ]);

  if (isLoading) {
    return (
      <div
        className="flex justify-center py-12"
        data-testid="folder-browser-loading"
      >
        <Spinner size={32} />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div
      data-testid="folder-browser"
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
      onContextMenu={handleBoxContextMenu}
      className="relative flex flex-1 flex-col"
    >
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept=".pdf,.txt,.rtf,.png,.jpg,.jpeg,.heic,.heif,.webp,.tiff,.tif,.doc,.docx,.odt,.csv,.xls,.xlsx,.ods,.pptx,.odp,.zip"
        className="hidden"
        data-testid="file-input"
        multiple
      />

      {/* Hidden folder input — selects a whole directory tree */}
      <input
        type="file"
        ref={folderInputEl}
        onChange={handleFileInput}
        className="hidden"
        data-testid="folder-input"
        webkitdirectory=""
        multiple
      />

      {/* Preparing overlay while the folder hierarchy is being created */}
      {isPreparing && (
        <div
          className="absolute inset-0 z-11 flex flex-col items-center justify-center gap-3 bg-surface-raised"
          data-testid="folder-upload-preparing"
        >
          <Spinner size={28} />
          <Text variant="body2" color="secondary">
            Preparing upload…
          </Text>
        </div>
      )}

      {/* Drop overlay for OS file uploads */}
      {isFileDropOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-border-focused bg-surface-raised">
          <Text variant="body1" className="text-content-accent">
            Drop files or folders here to upload
          </Text>
        </div>
      )}

      {/* Breadcrumb — show full path including root when inside a sub-folder */}
      {!hideBreadcrumb && data.breadcrumb.length > 1 && (
        <div className="px-4 py-2 sm:px-6">
          <Breadcrumbs data-testid="folder-breadcrumb">
            {data.breadcrumb.map((item, index) => {
              const isLast = index === data.breadcrumb.length - 1;
              if (isLast) {
                return (
                  <BreadcrumbsItem key={item.id ?? 'root'}>
                    <span className="type-body-md text-content-primary">
                      {item.name}
                    </span>
                  </BreadcrumbsItem>
                );
              }
              return (
                <BreadcrumbsItem key={item.id ?? 'root'}>
                  <button
                    type="button"
                    className={`cursor-pointer rounded-lg type-body-md transition-colors hover:bg-surface-raised ${
                      isDragOver(item.id)
                        ? 'bg-surface-raised'
                        : 'bg-transparent'
                    } px-1 text-content-primary underline hover:text-content-accent`}
                    onClick={() => onNavigate(item.id ?? undefined)}
                    {...getDropProps(item.id)}
                  >
                    {item.name}
                  </button>
                </BreadcrumbsItem>
              );
            })}
          </Breadcrumbs>
        </div>
      )}

      {/* Parent directory row with select-all checkbox */}
      <div>
        {(() => {
          if (folderId && data.breadcrumb.length >= 2) {
            const parentItem = data.breadcrumb[data.breadcrumb.length - 2];
            const parentId = parentItem.id;
            return (
              <div
                role="button"
                tabIndex={0}
                onClick={() => onNavigate(parentId ?? undefined)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onNavigate(parentId ?? undefined);
                  }
                }}
                {...getDropProps(parentId)}
                className={`flex cursor-pointer items-center gap-3 border-b px-4 py-2 transition-colors sm:px-6 ${
                  isDragOver(parentId)
                    ? 'border-border-focused bg-surface-raised'
                    : 'border-border-default bg-transparent'
                }`}
                data-testid="parent-folder-row"
              >
                {allItems.length > 0 && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                    data-testid="select-all-checkbox"
                  >
                    <TableCheckbox
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                  </div>
                )}
                <Text variant="body2" color="secondary">
                  ..
                </Text>
              </div>
            );
          }
          if (!onNavigateUp) {
            return null;
          }
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={onNavigateUp}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onNavigateUp();
                }
              }}
              className="flex cursor-pointer items-center gap-3 border-b border-border-default px-4 py-2 hover:bg-surface-raised sm:px-6"
              data-testid="parent-folder-row"
            >
              {allItems.length > 0 && (
                <div
                  onClick={e => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  data-testid="select-all-checkbox"
                >
                  <TableCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                  />
                </div>
              )}
              <Text variant="body2" color="secondary">
                ..
              </Text>
            </div>
          );
        })()}
      </div>

      {/* Content */}
      {data.folders.length === 0 && data.files.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 py-12"
          data-testid="folder-empty-state"
        >
          <InsertDriveFileIcon
            className="size-12 text-content-disabled"
            aria-hidden="true"
          />
          <Text variant="body2" color="secondary">
            This folder is empty
          </Text>
          {canWrite && (
            <>
              <Text variant="caption" color="secondary">
                Upload files or create a folder to get started
              </Text>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="empty-upload-button"
                >
                  <UploadFileIcon className="size-4" aria-hidden="true" />
                  Upload a file
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => folderInputEl.current?.click()}
                  data-testid="empty-upload-folder-button"
                >
                  <DriveFolderUploadOutlinedIcon
                    className="size-4"
                    aria-hidden="true"
                  />
                  Upload folder
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          {/* Folder rows */}
          {data.folders.map((folder, idx) => (
            <div
              key={folder.id}
              role="button"
              tabIndex={0}
              onClick={() => onNavigate(folder.id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onNavigate(folder.id);
                }
              }}
              onContextMenu={e =>
                handleContextMenu(e, { type: 'folder', item: folder })
              }
              {...getDragProps({ id: folder.id, type: 'folder' })}
              {...getDropProps(folder.id)}
              className={`flex cursor-pointer items-center gap-3 border-b px-4 py-3 transition-colors hover:bg-surface-raised sm:px-6 ${
                isDragOver(folder.id)
                  ? 'border-border-focused bg-surface-raised'
                  : 'border-border-default bg-transparent'
              }`}
              data-testid={`folder-row-${folder.id}`}
            >
              <div
                onClick={e => handleCheckboxClick(e, folder.id, 'folder', idx)}
                data-testid={`row-checkbox-${folder.id}`}
              >
                <TableCheckbox checked={isSelected(folder.id)} />
              </div>
              <FolderIcon
                className="size-5 text-content-secondary"
                aria-hidden="true"
              />
              <Text variant="body2" noWrap className="min-w-0 flex-1">
                {folder.name}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                aria-label="Folder actions"
                onClick={e =>
                  handleOverflowClick(e, { type: 'folder', item: folder })
                }
                data-testid={`row-actions-button-${folder.id}`}
              >
                <MoreVertIcon className="size-4" aria-hidden="true" />
              </Button>
            </div>
          ))}

          {/* File rows */}
          {data.files.map((file, idx) => (
            <div
              key={file.id}
              role="button"
              tabIndex={0}
              onClick={() => openPanel('file', file.id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openPanel('file', file.id);
                }
              }}
              onContextMenu={e =>
                handleContextMenu(e, { type: 'file', item: file })
              }
              {...getDragProps({ id: file.id, type: 'file' })}
              className="flex cursor-pointer items-center gap-3 border-b border-border-default px-4 py-3 hover:bg-surface-raised sm:px-6"
              data-testid={`file-row-${file.id}`}
            >
              <div
                onClick={e =>
                  handleCheckboxClick(
                    e,
                    file.id,
                    'file',
                    data.folders.length + idx
                  )
                }
                data-testid={`row-checkbox-${file.id}`}
              >
                <TableCheckbox checked={isSelected(file.id)} />
              </div>
              {getFileIcon(file.contentType, 16)}
              <Text variant="body2" noWrap className="min-w-0 flex-1">
                {file.name}
              </Text>
              <Text
                variant="caption"
                color="secondary"
                className="hidden shrink-0 sm:block"
              >
                {formatFileSize(file.fileSizeBytes)}
              </Text>
              <Text variant="caption" color="secondary" className="shrink-0">
                {new Date(file.uploadedAt).toLocaleDateString()}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                aria-label="File actions"
                onClick={e =>
                  handleOverflowClick(e, { type: 'file', item: file })
                }
                data-testid={`row-actions-button-${file.id}`}
              >
                <MoreVertIcon className="size-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Context menu */}
      <ContextMenu
        open={menuState.open}
        onClose={closeMenu}
        anchorPosition={menuState.anchorPosition}
        anchorEl={menuState.anchorEl}
        items={getMenuItems()}
      />

      {/* Rename dialog */}
      <Dialog
        open={renameTarget !== null}
        onOpenChange={open => !open && setRenameTarget(null)}
      >
        <DialogContent>
          <DialogHeader>Rename {renameTarget?.type}</DialogHeader>
          <DialogBody>
            <Field label="Name" className="mt-2">
              <Input
                autoFocus
                value={renameName}
                onChange={e => setRenameName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleRename();
                  }
                }}
                maxLength={255}
                data-testid="rename-input"
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="neutral"
              onClick={handleRename}
              disabled={
                !renameName.trim() || renameName.trim() === renameTarget?.name
              }
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>Delete {deleteTarget?.type}?</DialogHeader>
          <DialogBody>
            {`"${deleteTarget?.name}" will be moved to the recycle bin.`}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              data-testid="delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (deleteTarget) {
                  if (deleteTarget.type === 'folder') {
                    deleteFolder.mutate(deleteTarget.id);
                  } else {
                    deleteFile.mutate(deleteTarget.id);
                  }
                  setDeleteTarget(null);
                }
              }}
              data-testid="delete-confirm-button"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
